import type { TFunction } from 'i18next';

/**
 * Compact "x ago" formatting for a session's `created_at`. Returns a finished
 * natural-language string (rendered through <Text>); falls back to a localized
 * date for anything older than a month. Empty string for missing/invalid input.
 *
 * Translated at the call site via the passed `t` (called fresh during render, so
 * it re-evaluates reactively on a language change); the English templates are the
 * i18n keys.
 */
export const relativeTime = (t: TFunction, iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 0) return t('just now'); // clock skew — don't show "in N minutes"
  if (sec < 45) return t('just now');

  const min = Math.round(sec / 60);
  if (min < 60) return min <= 1 ? t('1 minute ago') : t('{{n}} minutes ago', { n: min });

  const hr = Math.round(min / 60);
  if (hr < 24) return hr <= 1 ? t('1 hour ago') : t('{{n}} hours ago', { n: hr });

  const day = Math.round(hr / 24);
  if (day < 30) return day <= 1 ? t('yesterday') : t('{{n}} days ago', { n: day });

  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};
